import React, { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { Body2, EditableHours, formatHours, SkeletonLoader } from "@activecollab/components";
import classNames from "classnames";
import moment from "moment";
import PropTypes from "prop-types";
import { useEditTaskSplits } from "~/repository/useTaskSplits";
import { makeIsDayOffSelector } from "~/store/selectors/selector.availabilityRecords";
import { getIsWorkday } from "~/store/selectors/selector.daysOff";
import { timeStringToFloat } from "~/utils/date";
import { stopwatchEditValidator } from "~/utils/utils";
import FormatDate from "../Formatters/FormatDate";
import { MINUTE_DECIMAL } from "../Workload/context/WorkloadContext";

export const SplitTableRow = ({ estimate, userId, date, pending, taskId, disabled, maxEstimate, canEdit }) => {
  const getIsUserDayOff = useMemo(() => makeIsDayOffSelector(userId, date), [userId, date]);
  const isDayOff = useSelector(getIsUserDayOff);
  const isWorkday = useSelector((state) => getIsWorkday(state, date));
  const useEditTaskSplitsMutation = useEditTaskSplits(taskId);

  const isToday = useMemo(() => {
    return moment().isSame(moment.unix(date), "day");
  }, [date]);

  const onEditEstimate = useCallback(
    (e) => {
      const value = timeStringToFloat(e.target.value);

      useEditTaskSplitsMutation.mutate({ [moment.unix(date).utc().format("YYYY-MM-DD")]: value });
    },
    [date, useEditTaskSplitsMutation]
  );

  const isTimeEstimateValueValid = useCallback(
    (_value) => {
      if (stopwatchEditValidator(_value)) {
        const value = timeStringToFloat(_value);
        if (!isWorkday) {
          return value === 0;
        }
        return value <= maxEstimate;
      }

      return false;
    },
    [isWorkday, maxEstimate]
  );

  return (
    <div
      className={classNames(
        "task-split-table-row tw-flex tw-border tw-border-solid tw-border-[var(--color-theme-300)] tw-border-t-0",
        {
          weekend: !isWorkday,
          "day-off": isWorkday && isDayOff,
          today: isToday,
        }
      )}
      data-testid="split-row"
    >
      <div
        className={classNames("tw-flex tw-items-center tw-px-3 tw-h-full tw-flex-1 tw-gap-3", {
          "tw-border-0 tw-border-solid tw-border-l-[2px] tw-border-primary tw-text-[var(--color-primary)]": isToday,
        })}
      >
        <Body2
          color="secondary"
          weight={isToday ? 600 : "medium"}
          className={classNames("", {
            "tw-text-[var(--color-primary)]": isToday,
          })}
        >
          <span>{moment.unix(date).utc().format("ddd")},</span>&nbsp;
          <FormatDate value={date} />
        </Body2>
        {isDayOff && isWorkday ? <Body2 className="tw-text-[#DD3D51] ">OFF</Body2> : null}
      </div>
      <div
        className={classNames(
          "split-duration tw-flex tw-justify-center tw-items-center tw-w-[120px] tw-border-0 tw-border-solid tw-border-l tw-border-[var(--color-theme-300)]",
          {
            weekend: !isWorkday,
            "day-off": isWorkday && isDayOff,
            no_estimated: estimate < MINUTE_DECIMAL,
            estimated: estimate >= MINUTE_DECIMAL,
          }
        )}
      >
        {(isWorkday && ((!canEdit && !isDayOff) || canEdit)) || estimate > MINUTE_DECIMAL ? (
          <>
            {pending ? (
              <SkeletonLoader className="tw-w-full tw-max-w-[32px] tw-h-3 tw-mt-1" />
            ) : (
              <EditableHours
                inputProps={{
                  style: {
                    fontSize: 14,
                    fontWeight: 500,
                    color: estimate > MINUTE_DECIMAL ? "var(--color-theme-900)" : "var(--color-theme-500)",
                  },
                }}
                value={formatHours(estimate)}
                disabled={disabled}
                onSave={onEditEstimate}
                validation={isTimeEstimateValueValid}
                variant="Body2"
                weight="500"
              />
            )}
          </>
        ) : null}
      </div>
    </div>
  );
};

SplitTableRow.propTypes = {
  estimate: PropTypes.number,
  userId: PropTypes.number,
  date: PropTypes.number,
  pending: PropTypes.bool,
  taskId: PropTypes.number,
  disabled: PropTypes.bool,
  maxEstimate: PropTypes.number,
  canEdit: PropTypes.bool,
};
